import {PopQuizCompletedQuizzesQuery, QuizItemResponseStatus} from 'src/generated/graphql';

import {MS_PER_DAY, STALE_COURSE_DAYS} from '../constants';
import {CandidateSource, QuizCandidate} from '../types';
import {REFRESHER_COOLDOWN_DAYS, REQUIZ_COOLDOWN_DAYS} from './constants';

/**
 * Latest-attempt info for a single quiz, pulled in by the attempt-lookup query.
 */
export interface CandidateAttemptInfo {
    hasMetPassingScore: boolean;
    completionTime: string;
    /** Fraction (0-1) of items answered correctly on that attempt. */
    percentCorrect: number;
}

/**
 * Derives the fraction of correctly-answered items from an attempt's item responses
 */
export function computePercentCorrect(
    itemResponses: Array<{status: QuizItemResponseStatus}>,
): number {
    if (itemResponses.length === 0) {
        return 0;
    }
    const correctCount = itemResponses.filter(
        (response) => response.status === QuizItemResponseStatus.Correct,
    ).length;
    return correctCount / itemResponses.length;
}

export interface CandidateStrategyContext {
    enrollmentsData: PopQuizCompletedQuizzesQuery | undefined;
    wasQuizRecentlyOffered: (quizId: string) => boolean;
    /** Bypasses today's-access / staleness / de-dupe rules (the `?showPopQuiz` QA override). */
    force: boolean;
    attemptsById: Record<string, CandidateAttemptInfo> | undefined;
    /** `?quizId=` from the URL — only honored alongside `force` (i.e. `?showPopQuiz`). */
    forcedQuizId: string | undefined;
}

/**
 * Inspects the shared context and returns zero or more candidates worth offering, each
 * tagged with its own `source`. Strategies never rank, exclude, or know about each other.
 */
export type CandidateStrategy = (context: CandidateStrategyContext) => QuizCandidate[];

/**
 *  Helper function to check if an enrollment is fresh enough to be considered for a pop quiz.
 */
function isEnrollmentFreshEnough(
    enrollment: {lastAccessedTime?: string | null},
    force: boolean,
    todayDate: string,
): boolean {
    if (force || !enrollment.lastAccessedTime) {
        return true;
    }
    if (new Date(enrollment.lastAccessedTime).toDateString() === todayDate) {
        return false;
    }
    const daysSinceAccess =
        (Date.now() - new Date(enrollment.lastAccessedTime).getTime()) / MS_PER_DAY;
    return daysSinceAccess <= STALE_COURSE_DAYS;
}

/**
 * QA/debug strategy — forces a synthetic quiz via `?showPopQuiz&quizId=<id>`, bypassing real
 * enrollment data entirely. Ranked above every other tier (see `ranking.ts`) so it always wins
 * once present
 */
export const buildDebugCandidate: CandidateStrategy = ({force, forcedQuizId}) => {
    if (!force || !forcedQuizId) {
        return [];
    }
    const quizId = Number(forcedQuizId);
    if (!Number.isFinite(quizId)) {
        return [];
    }
    return [
        {
            quizId,
            quizTitle: 'Debug quiz',
            courseId: 0,
            courseTitle: 'Debug course',
            courseUrl: '/',
            courseProgress: 0,
            courseTopicId: null,
            source: 'debug',
        },
    ];
};

/**
 * Fallback strategy — returns every eligible candidate, in enrollment order
 */
export const buildDefaultCandidates: CandidateStrategy = ({
    enrollmentsData,
    wasQuizRecentlyOffered,
    force,
}) => {
    const enrollments = enrollmentsData?.me?.enrollments?.courseEnrollmentsPaged?.courseEnrollments;
    if (!enrollments) {
        return [];
    }

    const todayDate = new Date().toDateString();
    const candidates: QuizCandidate[] = [];

    for (const enrollment of enrollments) {
        if (!enrollment || !isEnrollmentFreshEnough(enrollment, force, todayDate)) {
            continue;
        }

        const completedItems = enrollment.completedItems;
        if (!completedItems) {
            continue;
        }
        for (const item of completedItems) {
            if (
                item?.__typename === 'Quiz' &&
                item.id &&
                (force || !wasQuizRecentlyOffered(item.id))
            ) {
                const course = enrollment.course;
                if (!course?.urlCourseTaking) {
                    continue;
                }
                candidates.push({
                    quizId: Number(item.id),
                    quizTitle: item.title,
                    courseId: Number(course.id),
                    courseTitle: course.title,
                    courseUrl: String(course.urlCourseTaking),
                    courseProgress: Math.round(Number(enrollment.completionPercentage) || 0),
                    courseTopicId: course.primaryTopic?.id ?? null,
                    source: 'default',
                });
                break;
            }
        }
    }

    return candidates;
};

/**
 * Shared walk for the attempt-gated strategies (requiz, refresher)
 */
function buildAttemptGatedCandidates(
    {enrollmentsData, wasQuizRecentlyOffered, force, attemptsById}: CandidateStrategyContext,
    source: CandidateSource,
    cooldownDays: number,
    matchesAttempt: (attempt: CandidateAttemptInfo) => boolean,
): QuizCandidate[] {
    const enrollments = enrollmentsData?.me?.enrollments?.courseEnrollmentsPaged?.courseEnrollments;
    if (!enrollments || !attemptsById) {
        return [];
    }

    const todayDate = new Date().toDateString();
    const candidates: QuizCandidate[] = [];

    for (const enrollment of enrollments) {
        if (!enrollment || !isEnrollmentFreshEnough(enrollment, force, todayDate)) {
            continue;
        }

        const completedItems = enrollment.completedItems;
        if (!completedItems) {
            continue;
        }

        for (const item of completedItems) {
            if (
                item?.__typename !== 'Quiz' ||
                !item.id ||
                (!force && wasQuizRecentlyOffered(item.id))
            ) {
                continue;
            }

            const attempt = attemptsById[item.id];
            if (!attempt || !matchesAttempt(attempt)) {
                continue;
            }

            const daysSinceAttempt =
                (Date.now() - new Date(attempt.completionTime).getTime()) / MS_PER_DAY;
            if (!force && daysSinceAttempt < cooldownDays) {
                continue;
            }

            const course = enrollment.course;
            if (!course?.urlCourseTaking) {
                continue;
            }

            candidates.push({
                quizId: Number(item.id),
                quizTitle: item.title,
                courseId: Number(course.id),
                courseTitle: course.title,
                courseUrl: String(course.urlCourseTaking),
                courseProgress: Math.round(Number(enrollment.completionPercentage) || 0),
                courseTopicId: course.primaryTopic?.id ?? null,
                source,
                lastAttempt: attempt,
            });
            break;
        }
    }

    return candidates;
}

/**
 * Resurfaces a quiz the user previously failed, once REQUIZ_COOLDOWN_DAYS have passed since
 * that failed attempt — a lightweight spaced-repetition nudge
 */
export const buildRequizCandidates: CandidateStrategy = (context) =>
    buildAttemptGatedCandidates(
        context,
        'requiz',
        REQUIZ_COOLDOWN_DAYS,
        (attempt) => !attempt.hasMetPassingScore,
    );

/**
 * Resurfaces a quiz the user previously passed, once REFRESHER_COOLDOWN_DAYS have passed
 * since that attempt, as a refresher nudge back into a lapsed-but-unfinished course
 */
export const buildRefresherCandidates: CandidateStrategy = (context) =>
    buildAttemptGatedCandidates(
        context,
        'refresher',
        REFRESHER_COOLDOWN_DAYS,
        (attempt) => attempt.hasMetPassingScore,
    );
